// Company ship-to address book — the customer-facing manager.
//
// Lists the Company's shared saved delivery addresses (the same set the
// configurator's Shipping picker offers) and lets the customer prune addresses
// the company no longer ships to. The book is COMPANY-scoped — everyone at the
// company shares it — so a change here is visible to every teammate.
//
// The Company DEFAULT is ERP-owned: it renders with a "Default" badge and NO
// remove control, and the backend refuses to remove it even if asked (only
// Nexus changes the default). Removal is customer-initiated for every other
// address. This page never creates/edits an address inline — a new address
// enters the book by being used on an order (the write-back at submit); this
// surface is for review + pruning.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { SHIP_TO_COUNTRIES, type CompanyShipToAddress } from 'shared';

import { getShipToAddresses, removeShipToAddress } from '../api/addresses.js';
import { extractApiError } from '../api/client.js';

const COUNTRY_NAME_BY_ISO2 = new Map(SHIP_TO_COUNTRIES.map((c) => [c.iso2, c.name]));

// Multi-line display of one saved address, customer-voice only.
function addressLines(a: CompanyShipToAddress): string[] {
  const lines: string[] = [];
  if (a.location_name) lines.push(a.location_name);
  if (a.attention_name) lines.push(`Attn: ${a.attention_name}`);
  if (a.street_line_1) lines.push(a.street_line_1);
  if (a.street_line_2) lines.push(a.street_line_2);
  if (a.street_line_3) lines.push(a.street_line_3);
  const cityLine = [a.city, a.state_or_province, a.postal_code].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  if (a.country_iso2) lines.push(COUNTRY_NAME_BY_ISO2.get(a.country_iso2) ?? a.country_iso2);
  if (a.location_phone) lines.push(a.location_phone);
  return lines;
}

export function AddressBook(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ship-to-addresses'],
    queryFn: getShipToAddresses,
  });

  // Per-row removal state so one failing/removing row doesn't block the others.
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  async function handleRemove(a: CompanyShipToAddress): Promise<void> {
    const label = a.label ?? a.location_name ?? 'this address';
    if (!confirm(`Remove ${label} from your company’s saved addresses?`)) return;
    setRowError(null);
    setRemovingId(a.address_id);
    try {
      const next = await removeShipToAddress(a.address_id);
      // Seed both this page and the configurator picker with the refreshed book.
      queryClient.setQueryData(['ship-to-addresses'], next);
    } catch (err) {
      const message =
        extractApiError(err)?.message ??
        'We couldn’t remove that address. Please try again in a moment.';
      setRowError({ id: a.address_id, message });
    } finally {
      setRemovingId(null);
    }
  }

  const addresses = data ?? [];

  return (
    <div className="card">
      <h2>Saved shipping addresses</h2>
      <p className="help" style={{ marginTop: 0 }}>
        These are the delivery addresses your company can reuse when placing an order. Everyone at
        your company shares this list. A new address is saved automatically the first time it&apos;s
        used on an order.
      </p>

      {isLoading && <p style={{ marginTop: 16 }}>Loading…</p>}
      {error && (
        <div className="banner banner--error" style={{ marginTop: 16 }}>
          We couldn&apos;t load your saved addresses. Please refresh.
        </div>
      )}
      {data && addresses.length === 0 && (
        <p style={{ marginTop: 16 }}>
          You don&apos;t have any saved addresses yet. The first address you ship to will be saved
          here for next time.
        </p>
      )}

      {addresses.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'grid', gap: 12 }}>
          {addresses.map((a) => (
            <li
              key={a.address_id}
              className="card"
              style={{ margin: 0, display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>
                  {a.label ?? a.location_name ?? 'Saved address'}
                  {a.is_default && (
                    <span
                      className="badge"
                      style={{
                        marginLeft: 8,
                        background: 'var(--color-accent-bg)',
                        border: '1px solid var(--color-accent)',
                        color: 'var(--color-text-strong)',
                      }}
                      title="Your company’s default address, managed by Argo."
                    >
                      Default
                    </span>
                  )}
                </div>
                <address style={{ fontStyle: 'normal', marginTop: 4, color: 'var(--color-text-muted, #555)' }}>
                  {addressLines(a).map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </address>
                {rowError?.id === a.address_id && (
                  <p className="field-error" style={{ marginTop: 8 }}>
                    {rowError.message}
                  </p>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                {a.is_default ? (
                  // The default is ERP-owned — no remove control. Contacting us
                  // is the path to change it (matches the backend guard).
                  <span className="help">Contact us to change your default</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleRemove(a)}
                    disabled={removingId === a.address_id}
                  >
                    {removingId === a.address_id ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
